import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from 'js-cookie';
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import IdealTimer from "../screens/SessionExpiration";
import { getCookie, getLocalStorageItem, getAbsoluteExpiry } from "../utils/Utils";
import { useIdleTimer } from "react-idle-timer";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(null);
    const navigate = useNavigate();
    const refreshTimeout = useRef(null);
    const isRefreshing = useRef(false);
    const logoutTimeout = useRef(null);
    const isPopupShown = useRef(false);
    const channelRef = useRef(null);
    const showPopupRef = useRef(null);
    const [showPopup, setShowPopup] = useState(false);

    const IDLE_THRESHOLD = 45 * 60 * 1000; // 45 min 
    const REFRESH_BUFFER = 5 * 60 * 1000; // 5 min before expiry
    const LOGOUT_DELAY = 5 * 60 * 1000;  // 5 min after popup shown

    // sendEvent now accepts payload for tokenId and role
    const sendEvent = (type, payload = {}) => {
        if (channelRef.current) {
            channelRef.current.postMessage({ type, ...payload });
        } else {
            localStorage.setItem("auth-event", JSON.stringify({ type, ...payload, ts: Date.now() }));
        }
    };

    const { start, pause, getLastActiveTime, reset } = useIdleTimer({
        timeout: IDLE_THRESHOLD,
        crossTab: true,
        syncTimers: 200,
        leaderElection: true,
        events: ['mousemove', 'keydown', 'wheel', 'mousedown', 'touchstart', 'scroll'],
        stopOnIdle: false,
        startManually: true,
        onIdle: () => {
            console.log("All tabs idle → show popup");
            showPopupRef.current?.();
        },
        onActive: () => {
            console.log("User active again");
            isPopupShown.current = false;
        }
    });

    const logout = () => {
        pause();

        const tokenId = getCookie("idcs_id_token");
        const roleCookie = getCookie("role");
        const roleStorage = getLocalStorageItem("role");
        const role = roleCookie || roleStorage;

        //  Broadcast WITH tokenId and role in payload
        // so other tabs have the values even after this tab wipes cookies
        sendEvent("IDLE_LOGOUT", { tokenId, role });

        clearLocalStorage();
        removeCookies();

        if (!tokenId || !role) {
            window.location.replace("/session-timeout");
            return;
        }

        const baseUrl = process.env.REACT_APP_ANOTHER_API_URL;
        const logoutUrl =
            process.env.NODE_ENV === "development"
                ? `${baseUrl}/logout?port=${window.location.port}&role=${role}&token-id=${tokenId}&redirect=/session-timeout`
                : `${baseUrl}/logout?role=${role}&token-id=${tokenId}&redirect=/session-timeout`;

        setShowPopup(false);
        window.location.replace(logoutUrl);
    };

    const clearLocalStorage = () => {
        localStorage.clear();
        sessionStorage.clear();
    };

    const removeCookies = () => {
        Cookies.remove('idcs_user_assertion', { path: '/' });
        Cookies.remove('idcs_refresh_token', { path: '/' });
        Cookies.remove('idcs_id_token', { path: '/' });
        Cookies.remove('role', { path: '/' });
    };

    const stayLoggedIn = () => {
        setShowPopup(false);
        isPopupShown.current = false;
        if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
        sendEvent("REFRESH");
        refreshToken();
        reset();
    };

    const showPopupAndStartLogoutTimer = () => {
        if (isPopupShown.current) return;

        isPopupShown.current = true;
        setShowPopup(true);
        sendEvent("SHOW_POPUP");

        if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
        logoutTimeout.current = setTimeout(() => {
            logout();
        }, LOGOUT_DELAY);
    };

    
    useEffect(() => {
        showPopupRef.current = showPopupAndStartLogoutTimer;
    });

    const handleSessionCheck = (expTime) => {
        const timeLeft = expTime - Date.now();
        const lastActive = getLastActiveTime();
        const idleTime = Date.now() - lastActive;


        if (timeLeft <= REFRESH_BUFFER) {
            if (idleTime < IDLE_THRESHOLD) {
                refreshToken();
            } else {
                showPopupAndStartLogoutTimer();
            }
        }
    };

    const scheduleTokenRefresh = (absoluteExpTime) => {
        if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
        const delay = absoluteExpTime - Date.now() - REFRESH_BUFFER;
        if (delay <= 0) {
            handleSessionCheck(absoluteExpTime);
            return;
        }
        refreshTimeout.current = setTimeout(() => {
            handleSessionCheck(absoluteExpTime);
        }, delay);
    };

    const refreshToken = async () => {
        if (isRefreshing.current) return;
        isRefreshing.current = true;
        try {
            const response = await axiosInstance.post(`/api/auth/refresh-token`, {
                refreshToken: Cookies.get('idcs_refresh_token'),
                role: Cookies.get('role')
            });
            if (response?.status === 200 && response?.data?.statusCode === 200) {
                const absoluteExpTime = getAbsoluteExpiry({
                    expiresIn: response.data.data.expires_in
                });
                Cookies.set('idcs_user_assertion', response.data.data.access_token);
                Cookies.set('idcs_refresh_token', response.data.data.refresh_token);
                localStorage.setItem('sessionExpTime', absoluteExpTime);
                isPopupShown.current = false;
                setShowPopup(false);
                scheduleTokenRefresh(absoluteExpTime);
                start();
            }
        } catch (error) {
            logout();
        } finally {
            isRefreshing.current = false;
        }
    };

    const checkTokenValidity = async () => {
        const authToken = Cookies.get('idcs_user_assertion');
        const userRole = Cookies.get('role');
        if (!authToken || !userRole) return;
        const storedExpTime = localStorage.getItem('sessionExpTime');
        if (storedExpTime) {
            scheduleTokenRefresh(storedExpTime);
            return;
        }
        try {
            const response = await axiosInstance.post(`/api/auth/check-token`, {
                token: authToken,
                role: userRole
            });
            if (response?.status === 200) {
                const expTime = response?.data?.expirationTime;
                if (expTime) {
                    const absoluteExpTime = getAbsoluteExpiry({ expTime });
                    localStorage.setItem('sessionExpTime', absoluteExpTime);
                    scheduleTokenRefresh(absoluteExpTime);
                }
            }
        } catch (error) {
            logout();
        }
    };

    useEffect(() => {
        //  handleEvent receives full event object, not just type
        const handleEvent = (type, event = {}) => {
            if (!type) return;

            if (type === "LOGOUT") {
                clearLocalStorage();
                removeCookies();
                window.location.replace("/");
            }

            if (type === "IDLE_LOGOUT") {

               
                const tokenId = event?.tokenId;
                const role = event?.role;

                clearLocalStorage();
                removeCookies();

                if (!tokenId || !role) {
                    window.location.replace("/session-timeout");
                    return;
                }

                const baseUrl = process.env.REACT_APP_ANOTHER_API_URL;
                const logoutUrl =
                    process.env.NODE_ENV === "development"
                        ? `${baseUrl}/logout?port=${window.location.port}&role=${role}&token-id=${tokenId}&redirect=/session-timeout`
                        : `${baseUrl}/logout?role=${role}&token-id=${tokenId}&redirect=/session-timeout`;

                window.location.replace(logoutUrl);
            }

            if (type === "SHOW_POPUP") {
                if (!isPopupShown.current) {
                    isPopupShown.current = true;
                    setShowPopup(true);
                }
            }

            if (type === "REFRESH") {
                const exp = Number(localStorage.getItem("sessionExpTime"));
                if (exp) scheduleTokenRefresh(exp);
                setShowPopup(false);
                isPopupShown.current = false;
                if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
            }
        };

        if ("BroadcastChannel" in window) {
            const channel = new BroadcastChannel("auth-channel");
            channelRef.current = channel;
           
            channel.onmessage = (e) => handleEvent(e.data?.type, e.data);
            channel.onmessageerror = (err) => console.error("BroadcastChannel error:", err);
        } else {
            const handler = (e) => {
                if (e.key === "auth-event") {
                    try {
                        const parsed = JSON.parse(e.newValue);
                        
                        handleEvent(parsed?.type, parsed);
                    } catch {}
                }
            };
            window.addEventListener("storage", handler);
            return () => window.removeEventListener("storage", handler);
        }

        return () => channelRef.current?.close();
    }, []);

    //  Start/pause timer on mount
    useEffect(() => {
        const authToken = Cookies.get('idcs_user_assertion');
        const role = Cookies.get('role');
        if (authToken && role) {
            console.log("Auth present → start timer");
            start();
        } else {
            console.log("No auth → pause timer");
            pause();
        }
    }, []);

    //  Visibility change + initial token check 
    useEffect(() => {
        checkTokenValidity();
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const exp = Number(localStorage.getItem('sessionExpTime'));
                if (!exp) return;
                const timeLeft = exp - Date.now();
                if (timeLeft <= 0) {
                    logout();
                } else if (timeLeft <= REFRESH_BUFFER) {
                    handleSessionCheck(exp);
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
            if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
        };
    }, []);

    return (
        <AuthContext.Provider value={{ token, setToken, sendEvent }}>
            {children}
            {showPopup && (
                <IdealTimer
                    stayLoggedIn={stayLoggedIn}
                    logout={logout}
                />
            )}
        </AuthContext.Provider>
    );
};